import type {
  AppPlatformWithPolicy,
  PublicConfig,
} from '../../api/actions/appConfig';
import type {AppVersionPolicy} from '../../utils/AppVersion';
import {isSyncEnabled} from '../../utils/MyWeather';
import {CommonRootState} from '../types';
import {getDebugEmail} from '../user/selectors';

/**
 * A feature flag, fail-closed. Flags come from /me for the session, and the
 * persisted slice can outlive the session token (seen on next: signed out,
 * the top bar still offered "Cartes de prévision"), so a flag only counts
 * while signed in.
 */
const isFlagOn = (state: CommonRootState, name: string) =>
  state.user?.token != null && (state.featureFlags?.flags?.[name] ?? false);

// The forecast-map browse pages, /forecast-maps/zones and /models, and
// their links in Map settings (backend flag `forecast_map_browse`, #327 P;
// was `forecast_maps`). Forecast itself is a mode of the unified /map and
// needs no flag. Fail-closed: a missing flag map (anon / not yet fetched)
// must hide the feature.
export const selectForecastMapBrowseEnabled = (state: CommonRootState) =>
  isFlagOn(state, 'forecast_map_browse');

// Forecast map composer/builder surface (backend flag `forecast_map_builder`).
// Defined ahead of the composer MR; same fail-closed rule.
export const selectForecastMapBuilderEnabled = (state: CommonRootState) =>
  isFlagOn(state, 'forecast_map_builder');

/**
 * A kill switch the backend serves both ways: as a public flag
 * (`/api/app/flags`, kept across a sign-out, so it reaches signed-out users)
 * and, for older builds, in /me's session flags. Either one on kills.
 */
const isKillSwitchOn = (state: CommonRootState, name: string) =>
  (state.featureFlags?.publicFlags?.[name] ?? false) || isFlagOn(state, name);

// Forecast mode on the mobile weather map (#338) is everyone's, signed in or
// out (GFS for everyone; other models need Premium);
// `mobile_forecast_map_off` is its kill switch (backend !714 seeds it off).
// Negative, so it fails OPEN: no flags at all is not killed. A public flag
// since GFS went to signed-out users, so the switch reaches them too. The
// preview flag `mobile_forecast_map_preview` is no longer read (only the
// 1.62.99 internal builds did).
export const selectMobileForecastMapKilled = (state: CommonRootState) =>
  isKillSwitchOn(state, 'mobile_forecast_map_off');

// The mobile forecast wind particles' own kill switch (#62 S4-7; backend
// !731 seeds it off). The particle overlay is the app's only per-frame
// UI-thread worklet + Skia drawing path, and a crash after the map has loaded
// escapes the MapLibre crash-loop breaker, which only covers the start
// window; this turns the particles off without hiding forecast mode.
// Negative, so it fails OPEN, and public like the forecast switch.
export const selectMobileForecastParticlesKilled = (state: CommonRootState) =>
  isKillSwitchOn(state, 'mobile_forecast_particles_off');

/**
 * The public app configuration as stored (#379). Both apps keep it across a
 * sign-out. A state persisted before 1.64 has no rollouts or versions yet.
 */
export const selectPublicConfig = (state: CommonRootState): PublicConfig => ({
  flags: state.featureFlags?.publicFlags ?? {},
  rollouts: state.featureFlags?.rollouts ?? {},
  appVersions: state.featureFlags?.appVersions ?? {},
});

/**
 * The server's minimum and recommended version for `platform`, if any (#379):
 * the app suggests an update below `recommended` and blocks below `minimum`
 * (`evaluateUpdatePolicy`). `null` when nothing was fetched yet.
 */
export const selectAppVersionPolicy = (
  state: CommonRootState,
  platform: AppPlatformWithPolicy,
): AppVersionPolicy | null =>
  state.featureFlags?.appVersions?.[platform] ?? null;

// Per session too: a leftover config showed signed-out visitors Stripe
// subscribe buttons whose checkout then failed with a 401.
export const selectStripeConfig = (state: CommonRootState) =>
  state.user?.token != null ? (state.featureFlags?.stripeConfig ?? null) : null;

// This device's places and My weather order follow the account (backend#266,
// FE #381). Session-only, like every /me flag.
export const selectMyWeatherSyncEnabled = (state: CommonRootState) =>
  state.user?.token != null && isSyncEnabled(state.featureFlags?.flags);

// Whether the My weather editor may open, or stay open. From 1.65 the list
// is everyone's, with no flag (#381), and so is its editor: signed out or
// with the sync off its edits stay on this device. Only Login As (#420)
// closes it, where the list is another account's while the places and the
// My weather order on this device are the staff member's own.
export const selectMyWeatherEditable = (state: CommonRootState) =>
  !getDebugEmail(state);
